import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { frDate } from "@/lib/constants";
import { CategoryDot } from "@/lib/categoryColors";

function groupByCategory(items) {
  const map = new Map();
  items.forEach((it) => { const k = (it.item_category || "").trim(); map.set(k, [...(map.get(k) || []), it]); });
  return [...map.entries()].sort(([a], [b]) => (!a ? 1 : !b ? -1 : a.localeCompare(b, "fr")))
    .map(([k, list]) => [k, [...list].sort((x, y) => (x.item_name || "").localeCompare(y.item_name || "", "fr"))]);
}
import { Input } from "@/components/ui/input";
import { Truck, CheckCircle2, Circle, PackageCheck, RefreshCw, CalendarClock } from "lucide-react";

export default function LoadingList() {
  const { org } = useAuth();
  const warehouses = org?.warehouses || [];
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [whFilter, setWhFilter] = useState("all");
  const [data, setData] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/loading?date=${date}`);
    setData(data);
    setRefreshedAt(new Date());
  }, [date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async (item) => {
    const next = item.status === "charge" ? "reserve" : "charge";
    setData((d) => ({
      ...d,
      events: d.events.map((ev) => ({
        ...ev,
        items: ev.items.map((i) => (i.res_id === item.res_id ? { ...i, status: next } : i)),
        loaded_count: ev.items.filter((i) => (i.res_id === item.res_id ? next : i.status) === "charge" || (i.res_id !== item.res_id && i.status === "retourne")).length,
      })),
    }));
    await api.patch(`/reservations/${item.res_id}/status`, { status: next });
    load();
  };

  const rawEvents = data?.events || [];
  const events = whFilter === "all"
    ? rawEvents
    : rawEvents
        .map((ev) => ({ ...ev, items: ev.items.filter((i) => (i.location_id || "") === whFilter) }))
        .filter((ev) => ev.items.length > 0);
  const totalItems = events.reduce((s, e) => s + e.items.length, 0);
  const loadedItems = events.reduce((s, e) => s + e.items.filter((i) => i.status === "charge" || i.status === "retourne").length, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF6A00]/15"><Truck className="h-6 w-6 text-[#FF6A00]" strokeWidth={1.6} /></div>
            <div>
              <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Chargement du jour</h1>
              <p className="mt-1 text-sm text-zinc-500">Matériel à sortir, groupé par événement — vue entrepôt.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {warehouses.length > 0 && (
            <select data-testid="loading-warehouse-filter" value={whFilter} onChange={(e) => setWhFilter(e.target.value)}
              className="rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300">
              <option value="all">Tous les entrepôts</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <Input data-testid="loading-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 rounded-full border-zinc-800 bg-zinc-900" />
          <button data-testid="loading-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-zinc-800 px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>
      </div>

      {totalItems > 0 && (
        <div className="flex items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <PackageCheck className="h-8 w-8 text-[#FFD500]" />
          <div className="flex-1">
            <div className="flex items-baseline justify-between">
              <span className="font-display text-2xl font-bold text-white">{loadedItems}/{totalItems}</span>
              <span className="text-sm text-zinc-500">articles chargés</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-800">
              <div className="h-full rounded-full bg-gradient-to-r from-[#FF6A00] to-[#FFD500] transition-all duration-500" style={{ width: `${totalItems ? (loadedItems / totalItems) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-20 text-center">
          <CalendarClock className="mx-auto h-14 w-14 text-zinc-700" />
          <p className="mt-4 text-xl font-medium text-white">Rien à charger pour le {frDate(date)}</p>
          <p className="mt-1 text-zinc-500">Aucun matériel réservé sur cette date.</p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {events.map((ev) => {
            const evLoaded = ev.items.filter((i) => i.status === "charge" || i.status === "retourne").length;
            const evTotal = ev.items.length;
            const done = evTotal > 0 && evLoaded === evTotal;
            return (
              <div key={ev.event_id} data-testid={`loading-event-${ev.event_id}`} className={`rounded-2xl border bg-zinc-900 p-6 ${done ? "border-emerald-500/40" : "border-zinc-800"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-white">{ev.event_title}</h2>
                    <p className="text-zinc-500">{ev.contact_name || "—"} · {ev.event_date ? frDate(ev.event_date) : ""}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-sm font-semibold ${done ? "bg-emerald-500/15 text-emerald-400" : "bg-[#FF6A00]/10 text-[#FF6A00]"}`}>
                    {evLoaded}/{evTotal} chargés
                  </span>
                </div>
                <div className="mt-4 space-y-4">
                  {groupByCategory(ev.items).map(([cat, list]) => {
                    const n = list.filter((i) => i.status === "charge" || i.status === "retourne").length;
                    const slug = (cat || "none").replace(/\W+/g, "-");
                    return (
                      <div key={cat || "none"} data-testid={`load-group-${ev.event_id}-${slug}`} className="space-y-2">
                        <div className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                          <CategoryDot org={org} name={cat} />
                          <span className="flex-1">{cat || "Sans catégorie"}</span>
                          <span data-testid={`load-group-count-${ev.event_id}-${slug}`} className={n === list.length ? "text-emerald-400" : ""}>{n}/{list.length}</span>
                        </div>
                  {list.map((it) => {
                    const loaded = it.status === "charge" || it.status === "retourne";
                    return (
                      <button key={it.res_id} data-testid={`load-item-${it.res_id}`} onClick={() => it.status !== "retourne" && toggle(it)} disabled={it.status === "retourne"}
                        className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${loaded ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                        {loaded ? <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-emerald-400" /> : <Circle className="h-6 w-6 flex-shrink-0 text-zinc-600" />}
                        <span className={`flex-1 text-lg font-medium ${loaded ? "text-emerald-300 line-through" : "text-white"}`}>{it.item_name}{it.location ? <span className="ml-2 rounded-full bg-[#FF6A00]/10 px-2 py-0.5 text-xs font-normal text-[#FF6A00]">{it.location}</span> : null}</span>
                        <span className="text-lg font-bold text-zinc-400">×{it.quantity}</span>
                        {it.status === "retourne" && <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-400">Rendu</span>}
                      </button>
                    );
                  })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {refreshedAt && <p className="text-center text-xs text-zinc-600">Actualisé à {refreshedAt.toLocaleTimeString("fr-FR")} · rafraîchissement automatique toutes les 20s</p>}
    </div>
  );
}
